// Fiche institution « Mairie » — maquette « Mairie de Cocody » (13 mairies du District d'Abidjan).
// Données réelles : maires 2023-2028, population RGPH ≈ ; contenus actualités/projets/équipements illustratifs (signalés).
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Search, MapPin, Bell, User, BadgeCheck, Landmark, Share2, Bookmark, Star,
  MessageSquare, Route, LayoutGrid, Newspaper, Target, Dumbbell, FileText, Image as ImageIcon,
  Home, FileBadge, Building2, CalendarDays, Leaf, Users, Laptop, ChevronRight, ArrowRight,
  Globe, Phone, Clock, Calendar, CircleHelp, Store, Coins, Heart, Flower2,
} from "lucide-react";
import { LocalPackMap } from "@/components/LocalPackMap";
import "./mairie.css";

const API = process.env.REACT_APP_BACKEND_URL;

const SVC_ICON = { file: FileBadge, building: Building2, calendar: CalendarDays, leaf: Leaf, users: Users, laptop: Laptop, rings: Heart, flower: Flower2, home: Home, coins: Coins, store: Store };

const TABS = [
  { id: "overview", label: "Vue d'ensemble", Icon: Home },
  { id: "services", label: "Services", Icon: LayoutGrid },
  { id: "actus", label: "Actualités", Icon: Newspaper },
  { id: "projets", label: "Projets", Icon: Target },
  { id: "equipements", label: "Équipements", Icon: Dumbbell },
  { id: "documents", label: "Documents", Icon: FileText },
  { id: "galerie", label: "Galerie", Icon: ImageIcon },
];

const initials = (n) => n.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const soon = (msg) => toast.info(msg || "Service en ligne bientôt disponible — la mairie est en cours d'intégration sur AYA.");

export default function MairiePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("overview");
  const [follow, setFollow] = useState(false);
  const [fullPres, setFullPres] = useState(false);
  const [dq, setDq] = useState("");
  const [dcat, setDcat] = useState("Toutes");

  useEffect(() => {
    setM(null); setErr(false); setTab("overview"); setFullPres(false);
    axios.get(`${API}/api/v3/search/mairie/${slug}`)
      .then((r) => (r.data?.mairie ? setM(r.data.mairie) : setErr(true)))
      .catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [slug]);

  const demarches = useMemo(() => {
    if (!m) return [];
    return m.demarches.filter((d) =>
      (dcat === "Toutes" || d.cat === dcat) &&
      (!dq || `${d.title} ${d.desc}`.toLowerCase().includes(dq.toLowerCase())));
  }, [m, dq, dcat]);

  const lowName = m ? m.name.replace(/^Mairie/, "mairie") : "";

  if (err) return (
    <div className="mr-page"><div className="mr-missing" data-testid="mairie-not-found">
      <Landmark size={34} color="#0E8A4F" />
      <h2>Fiche indisponible</h2>
      <p>Cette fiche mairie n'est pas encore disponible dans AYA.</p>
      <button className="mr-cta" onClick={() => navigate("/search")}>Retour à AYA Search</button>
    </div></div>
  );
  if (!m) return <div className="mr-page"><div className="mr-missing">Chargement de la fiche…</div></div>;

  const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${m.lat},${m.lng}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.name + " Abidjan")}`;
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: m.name, url }); } catch {} }
    else { await navigator.clipboard?.writeText(url); toast.success("Lien de la fiche copié"); }
  };
  const Prov = ({ children }) => <div className="mr-prov">{children || m.demo_note}</div>;

  const Stats = () => (
    <div className="mr-stats" data-testid="mairie-stats">
      {m.stats.population && <div className="mr-stat"><Users size={15} /><div><span>Population</span><b>{m.stats.population}</b></div></div>}
      {m.stats.superficie && <div className="mr-stat"><MapPin size={15} /><div><span>Superficie</span><b>{m.stats.superficie}</b></div></div>}
      <div className="mr-stat"><Calendar size={15} /><div><span>Statut</span><b>{m.stats.statut}</b></div></div>
    </div>
  );

  const ActuCard = ({ a }) => (
    <div className="mr-actu" data-testid={`mairie-actu-${a.id}`}>
      <img src={a.img} alt="" />
      <div className="bd">
        <div className="mt"><span className="dt">{a.date}</span><span className="tg">{a.cat}</span></div>
        <b>{a.title}</b><p>{a.desc}</p>
      </div>
    </div>
  );

  const ProjetCard = ({ p }) => (
    <div className="mr-projet" data-testid={`mairie-projet-${p.id}`}>
      <div className="im"><img src={p.img} alt="" /><span className={`st ${p.statut === "En cours" ? "on" : "plan"}`}>{p.statut}</span></div>
      <div className="bd">
        <b>{p.title}</b><p>{p.desc}</p>
        <span className="ct">{p.cat}</span>
        <div className="pg"><div className="bar"><i style={{ width: `${p.pct}%` }} /></div><span>{p.pct} %</span></div>
      </div>
    </div>
  );

  const EquipCard = ({ e }) => (
    <div className="mr-equip" data-testid={`mairie-equip-${e.id}`}>
      <img src={e.img} alt="" />
      <div className="bd"><span className="tg">{e.cat}</span><b>{e.name}</b><p>{e.desc}</p></div>
    </div>
  );

  const SectionHead = ({ icon, title, more, onMore }) => (
    <div className="mr-shead">
      <h2>{icon}{title}</h2>
      {more && <button className="mr-more" onClick={onMore}>{more} <ChevronRight size={13} /></button>}
    </div>
  );

  return (
    <div className="mr-page" data-testid={`mairie-page-${m.slug}`}>
      {/* Barre AYA */}
      <header className="mr-topbar">
        <button className="ic" data-testid="mairie-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={19} /></button>
        <span className="brand">AYA</span>
        <button className="searchbar" data-testid="mairie-searchbar" onClick={() => navigate("/search")}>
          <Search size={15} /> Rechercher une personnalité, une institution, un lieu…
        </button>
        <span className="loc"><MapPin size={14} /> Abidjan</span>
        <button className="ic"><Bell size={17} /></button>
        <span className="av"><User size={15} /></span>
      </header>

      {/* Héro */}
      <section className="mr-hero" style={{ backgroundImage: `linear-gradient(180deg, rgba(4,26,16,0.55), rgba(4,26,16,0.86)), url(${m.hero})` }}>
        <div className="mr-hero-actions">
          <button className="hic" onClick={share} data-testid="mairie-share-top"><Share2 size={16} /></button>
          <button className="hic" onClick={() => { setFollow(true); toast.success("Fiche enregistrée"); }}><Bookmark size={16} /></button>
        </div>
        <div className="mr-hero-grid">
          <div className="mr-hero-main">
            <span className="mr-badge" data-testid="mairie-badge">{m.badge}</span>
            <div className="mr-idrow">
              <span className="logo"><Landmark size={30} color="#0E8A4F" /></span>
              <div>
                <h1 data-testid="mairie-title">{m.name} <BadgeCheck size={21} color="#3B82F6" fill="#fff" /></h1>
                <p className="sub">{m.subtitle}</p>
                <p className="loc"><span className="flag"><i /><i /><i /></span> {m.location}</p>
              </div>
            </div>
            <p className="mr-tagline">« {m.tagline} »</p>
          </div>
          <aside className="mr-mayor" data-testid="mairie-maire-card">
            <span className="lbl">Le maire</span>
            <div className="row">
              <span className="pf">{initials(m.maire.name)}</span>
              <div>
                <b>{m.maire.name}</b>
                <span>{m.maire.role}</span>
                <span className="md">{m.maire.parti ? `${m.maire.parti} · ` : ""}{m.maire.mandat}</span>
              </div>
            </div>
            <button className="pill" data-testid="mairie-maire-fiche-btn" onClick={() => navigate(`/search?q=${encodeURIComponent(m.maire.name)}`)}>Voir sa fiche <ChevronRight size={13} /></button>
          </aside>
        </div>
        <Stats />
        <div className="mr-actionrow">
          <button className={`mr-cta ${follow ? "done" : ""}`} data-testid="mairie-follow-btn" onClick={() => { setFollow(!follow); toast.success(follow ? "Vous ne suivez plus cette institution" : `Vous suivez la ${m.name}`); }}>
            <Star size={14} /> {follow ? "Suivi" : "Suivre"}
          </button>
          <button className="mr-ghost" data-testid="mairie-share-btn" onClick={share}><Share2 size={14} /> Partager</button>
          <button className="mr-ghost" data-testid="mairie-contact-btn" onClick={() => soon(m.infos.contact_note)}><MessageSquare size={14} /> Contacter</button>
          <a className="mr-ghost" href={dirUrl} target="_blank" rel="noreferrer" data-testid="mairie-itinerary-btn"><Route size={14} /> Itinéraire</a>
        </div>
      </section>

      {/* Onglets */}
      <nav className="mr-tabs" data-testid="mairie-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} className={tab === id ? "on" : ""} data-testid={`mairie-tab-${id}`} onClick={() => setTab(id)}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </nav>

      <main className="mr-body">
        {tab === "overview" && (
          <>
            <div className="mr-cols">
              <div className="mr-card" data-testid="mairie-presentation">
                <h2 className="ttl">Présentation</h2>
                <p className="txt">{m.presentation}</p>
                {fullPres && <p className="txt">{m.histoire}</p>}
                <button className="mr-outline" data-testid="mairie-presentation-more" onClick={() => setFullPres(!fullPres)}>
                  {fullPres ? "Réduire" : "Lire la suite"} <ChevronRight size={13} style={{ transform: fullPres ? "rotate(-90deg)" : "rotate(90deg)" }} />
                </button>
                {m.maire.note && <p className="note">{m.maire.note}</p>}
              </div>
              <aside className="mr-card side" data-testid="mairie-infos">
                <h2 className="ttl">Informations pratiques</h2>
                <div className="inf"><MapPin size={15} color="#0E8A4F" /><div><b>{m.infos.adresse[0]}</b><span>{m.infos.adresse[1]}</span></div></div>
                <div className="inf"><Phone size={15} color="#0E8A4F" /><div><span>{m.infos.contact_note}</span></div></div>
                <h3 className="sttl"><Clock size={13} /> Horaires d'ouverture</h3>
                {m.infos.horaires.map(([d, h]) => <div className="hr" key={d}><span>{d}</span><b className={h === "Fermé" ? "off" : ""}>{h}</b></div>)}
                <p className="note">{m.infos.horaires_note}</p>
              </aside>
            </div>

            <SectionHead title="Services municipaux" more="Voir tous les services" onMore={() => setTab("services")} />
            <div className="mr-svcgrid" data-testid="mairie-services-grid">
              {m.services.map((s) => { const I = SVC_ICON[s.icon] || FileBadge; return (
                <button key={s.id} className="mr-svc" data-testid={`mairie-service-${s.id}`} onClick={() => setTab("services")}>
                  <span className="bub" style={{ background: s.color }}><I size={17} color="#fff" /></span>
                  <b>{s.label}</b><p>{s.desc}</p>
                </button>
              ); })}
            </div>

            <SectionHead title="Actualités de la mairie" more="Voir toutes les actualités" onMore={() => setTab("actus")} />
            <div className="mr-actugrid">{m.actualites.map((a) => <ActuCard key={a.id} a={a} />)}</div>
            <Prov />

            <SectionHead title="Projets en cours" more="Voir tous les projets" onMore={() => setTab("projets")} />
            <div className="mr-projgrid">{m.projets.map((p) => <ProjetCard key={p.id} p={p} />)}</div>
            <Prov />

            <div className="mr-cols even">
              <div>
                <SectionHead title="Équipements publics" more="Voir tout" onMore={() => setTab("equipements")} />
                <div className="mr-thumbs">
                  {m.equipements.slice(1, 5).map((e) => (
                    <button key={e.id} className="mr-thumb" onClick={() => setTab("equipements")}>
                      <img src={e.img} alt="" /><span>{e.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <SectionHead title="Localisation" />
                <div className="mr-card map" data-testid="mairie-map">
                  <div className="mp"><LocalPackMap results={[{ id: `mairie-${m.slug}`, name: m.name, lat: m.lat, lng: m.lng }]} selectedId={null} onSelect={() => {}} /></div>
                  <a className="mr-cta dark" href={mapsUrl} target="_blank" rel="noreferrer" data-testid="mairie-gmaps-btn"><MapPin size={14} /> Voir sur Google Maps</a>
                </div>
              </div>
            </div>

            <SectionHead title="Liens utiles" />
            <div className="mr-links" data-testid="mairie-links">
              {m.links.map((l) => (
                <a key={l.url} className="mr-link" href={l.url} target="_blank" rel="noreferrer">
                  <span className="fv"><Globe size={15} color="#2563EB" /></span>
                  <span className="bd"><b>{l.title}</b><span>{l.domain}</span></span>
                  <ChevronRight size={15} color="#9CA3AF" />
                </a>
              ))}
            </div>
          </>
        )}

        {tab === "services" && (
          <div className="mr-cols">
            <div>
              <div className="mr-card">
                <div className="mr-svchead">
                  <span className="bub lg" style={{ background: "#0E8A4F" }}><LayoutGrid size={19} color="#fff" /></span>
                  <div><h2 className="ttl">Démarches en ligne</h2><p className="txt" style={{ margin: 0 }}>Réalisez vos démarches administratives avec la {lowName} en ligne, simplement et rapidement.</p></div>
                </div>
                <div className="mr-dsearch">
                  <Search size={14} color="#9CA3AF" />
                  <input data-testid="mairie-demarche-search" placeholder="Rechercher une démarche (ex : acte de naissance, permis, taxe…)" value={dq} onChange={(e) => setDq(e.target.value)} />
                </div>
                <div className="mr-chips">
                  {["Toutes", "État civil", "Urbanisme", "Fiscalité", "Environnement", "Autres"].map((c) => (
                    <button key={c} className={dcat === c ? "on" : ""} data-testid={`mairie-demarche-cat-${c}`} onClick={() => setDcat(c)}>{c}</button>
                  ))}
                </div>
              </div>
              <div className="mr-demgrid" data-testid="mairie-demarches">
                {demarches.map((d) => { const I = SVC_ICON[d.icon] || FileBadge; return (
                  <div key={d.id} className="mr-dem" data-testid={`mairie-demarche-${d.id}`}>
                    <span className="bub" style={{ background: d.color }}><I size={16} color="#fff" /></span>
                    <b>{d.title}</b><p>{d.desc}</p>
                    <button className="mr-outline sm" onClick={() => soon()}>{d.cta} <ChevronRight size={12} /></button>
                  </div>
                ); })}
                {demarches.length === 0 && <p className="txt">Aucune démarche ne correspond à votre recherche.</p>}
              </div>
            </div>
            <aside>
              <div className="mr-card side" data-testid="mairie-howto">
                <h2 className="ttl"><CircleHelp size={15} style={{ verticalAlign: -2, color: "#0E8A4F" }} /> Comment ça marche ?</h2>
                {[["Sélectionnez la démarche", "Choisissez le service concerné."], ["Remplissez le formulaire", "Renseignez vos informations et ajoutez les pièces nécessaires."], ["Validez et suivez", "Recevez un numéro de dossier et suivez l'avancement en ligne."]].map(([t, d], i) => (
                  <div className="step" key={t}><span className="n">{i + 1}</span><div><b>{t}</b><p>{d}</p></div></div>
                ))}
              </div>
              <div className="mr-card side green">
                <h2 className="ttl">Toutes vos démarches sur mobile</h2>
                <p className="txt">Gagnez du temps avec l'application AYA. Accédez aux services de la {lowName}, où que vous soyez.</p>
              </div>
            </aside>
          </div>
        )}

        {tab === "actus" && (
          <>
            <SectionHead title={`Actualités de la ${lowName}`} />
            <div className="mr-actugrid wide">{m.actualites.map((a) => <ActuCard key={a.id} a={a} />)}</div>
            <Prov />
          </>
        )}

        {tab === "projets" && (
          <div className="mr-cols">
            <div>
              <SectionHead title={`Projets de la ${lowName}`} />
              <div className="mr-projgrid wide">{m.projets.map((p) => <ProjetCard key={p.id} p={p} />)}</div>
              <Prov />
            </div>
            <aside className="mr-card side" data-testid="mairie-domaines">
              <h2 className="ttl">Domaines prioritaires</h2>
              {m.domaines.map((d) => <div className="dom" key={d}><span className="dot" /> {d} <ChevronRight size={13} color="#9CA3AF" /></div>)}
            </aside>
          </div>
        )}

        {tab === "equipements" && (
          <>
            <SectionHead title={`Équipements de la ${lowName}`} />
            <div className="mr-equipgrid" data-testid="mairie-equipements">{m.equipements.map((e) => <EquipCard key={e.id} e={e} />)}</div>
            <Prov />
            <div className="mr-card map" style={{ marginTop: 12 }}>
              <div className="mp"><LocalPackMap results={[{ id: `mairie-${m.slug}`, name: m.name, lat: m.lat, lng: m.lng }]} selectedId={null} onSelect={() => {}} /></div>
              <a className="mr-cta dark" href={mapsUrl} target="_blank" rel="noreferrer"><MapPin size={14} /> Voir la carte interactive</a>
            </div>
          </>
        )}

        {tab === "documents" && (
          <>
            <SectionHead title="Documents utiles" />
            <div className="mr-links" data-testid="mairie-documents">
              {m.documents.map((d) => (
                <button key={d} className="mr-link" onClick={() => soon(`« ${d} » sera disponible dès l'intégration officielle de la ${lowName}.`)}>
                  <span className="fv"><FileText size={15} color="#0E8A4F" /></span>
                  <span className="bd"><b>{d}</b><span>Document de la mairie</span></span>
                  <ChevronRight size={15} color="#9CA3AF" />
                </button>
              ))}
            </div>
          </>
        )}

        {tab === "galerie" && (
          <>
            <SectionHead title="Galerie" />
            <div className="mr-gallery" data-testid="mairie-gallery">
              {m.galerie.map((src) => <img key={src} src={src} alt="" />)}
            </div>
            <Prov>Visuels d'illustration AYA — photos officielles de la mairie en cours d'intégration.</Prov>
          </>
        )}
      </main>

      {/* Pied d'actions */}
      <footer className="mr-foot" data-testid="mairie-footer">
        <button className="mr-outline" onClick={() => soon(m.infos.contact_note)}><MessageSquare size={14} /> Contacter la mairie</button>
        <button className="mr-outline" onClick={() => soon("La prise de rendez-vous en ligne arrive avec l'intégration officielle de la mairie.")}><Calendar size={14} /> Prendre rendez-vous</button>
        <button className="mr-cta" data-testid="mairie-footer-follow" onClick={() => { setFollow(true); toast.success(`Vous suivez la ${m.name}`); }}><Star size={14} /> Suivre cette institution</button>
      </footer>
    </div>
  );
}
